/**
 * Contexto de Drill-Down para cross-filtering entre componentes.
 *
 * Permite que a seleção de uma linha em uma tabela (ex: Grupo de Canais)
 * filtre automaticamente outras tabelas e gráficos na página.
 *
 * Hierarquias suportadas:
 * - Aquisição: channel_group → source_medium → campaign
 * - Geográfica: region → city → source_medium
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";

/* eslint-disable react-refresh/only-export-components */

/**
 * Tipos de dimensões disponíveis para drill-down.
 *
 * <M-212> `category2` a `category5`, `brand` e `variant` **saíram** da união. Elas não são
 * pedidas ao GA4 — o sincronizador parou de pedi-las porque as dimensões hierárquicas de item
 * inflavam a receita em ~25×, e a leitura de produção confirma que nenhuma linha as tem
 * preenchidas —, então `casaComFiltrosDeItem` e `casaComEstendidos` comparam o valor pedido
 * contra a string vazia e descartam **todas** as linhas. Enquanto elas estavam aqui, um
 * clique podia esvaziar a página inteira. Tirá-las da união põe o compilador de guarda: um
 * componente que volte a emitir uma delas não compila. Ver **M-138** e **D-034**.
 */
export type DrillDimension =
  | "channel_group"
  | "source_medium"
  | "session_source"
  | "session_medium"
  | "campaign"
  | "ad_content"
  | "region"
  | "city"
  | "category"
  | "product"
  | "device"
  | "search_term"
  | "date"
  | "new_returning"
  | "landing_page"
  | "gender"
  | "age"
  | "ad_platform";

/**
 * Filtro ativo baseado em uma seleção.
 * Suporta multi-seleção (Ctrl+click) via campo `values`.
 */
export interface DrillFilter {
  dimension: DrillDimension;
  /** Valor primário (último selecionado). Mantido para compat. */
  value: string;
  /** Todos os valores selecionados (multi-select). */
  values: string[];
  label?: string;
}

/**
 * Estado do contexto de drill-down
 */
interface DrilldownState {
  /** Filtros ativos por dimensão */
  filters: Record<string, DrillFilter>;
  /** Nível atual do drill-down (0 = nível raiz) */
  drillLevel: number;
  /** Caminho de navegação (breadcrumb) */
  breadcrumb: DrillFilter[];
}

/**
 * Interface do contexto de drill-down
 */
interface DrilldownContextValue extends DrilldownState {
  /**
   * Seleciona uma linha e aplica filtro cross-filter.
   * @param multiSelect - Se true (Ctrl+click), adiciona/remove valor sem limpar os demais.
   */
  selectRow: (
    dimension: DrillDimension,
    value: string,
    label?: string,
    multiSelect?: boolean,
  ) => void;
  /** Limpa um filtro específico */
  clearFilter: (dimension: DrillDimension) => void;
  /** Remove um valor específico de um filtro multi-select */
  clearFilterValue: (dimension: DrillDimension, value: string) => void;
  /** Limpa todos os filtros */
  clearAllFilters: () => void;
  /** Navega para um nível específico do breadcrumb */
  navigateTo: (level: number) => void;
  /**
   * Query params derivados dos filtros ativos (ex: { filter_category: 'Camisetas' }).
   * Multi-valores são separados por pipe `|`.
   * Deve ser tratado como read-only pelos consumidores.
   */
  apiFilters: Record<string, string>;
  /**
   * Assinatura estável (string) para uso em dependências de effects e keys.
   * Muda sempre que `apiFilters` mudar.
   */
  apiFiltersKey: string;
  /** Obtém os filtros como query params para API */
  getApiFilters: () => Record<string, string>;
  /** Verifica se um valor de uma dimensão está selecionado */
  isSelected: (dimension: DrillDimension, value: string) => boolean;
}

const DrilldownContext = createContext<DrilldownContextValue | undefined>(
  undefined,
);

interface DrilldownProviderProps {
  children: ReactNode;
  /**
   * Hierarquia de dimensões para navegação drill-down.
   * O primeiro item é o nível raiz.
   * @example ['channel_group', 'source_medium', 'campaign']
   */
  hierarchy?: DrillDimension[];
  /**
   * Múltiplas hierarquias (grupos) de drill-down/cross-filtering.
   *
   * Regra (consistente com BI / Looker):
   * - Seleções dentro do MESMO grupo podem acumular (ex.: channel_group → source_medium → campaign).
   * - Ao selecionar uma dimensão de OUTRO grupo, as seleções anteriores são removidas.
   *
   * Se omitido, o provider usa `hierarchy` como único grupo.
   *
   * @example
   * [
   *   ['category', 'product'],
   *   ['region', 'city'],
   *   ['device'],
   * ]
   */
  hierarchies?: DrillDimension[][];
}

/**
 * Provider do contexto de drill-down.
 *
 * @example
 * ```tsx
 * <DrilldownProvider hierarchy={['channel_group', 'source_medium', 'campaign']}>
 *   <AcquisitionPage />
 * </DrilldownProvider>
 * ```
 */
export function DrilldownProvider({
  children,
  hierarchy = ["channel_group", "source_medium", "campaign"],
  hierarchies,
}: DrilldownProviderProps) {
  const hierarchiesList = useMemo((): DrillDimension[][] => {
    if (hierarchies && hierarchies.length > 0) {
      return hierarchies;
    }
    return [hierarchy];
  }, [hierarchies, hierarchy]);

  const [filters, setFilters] = useState<Record<string, DrillFilter>>({});

  const activeHierarchyIndex = useMemo((): number | null => {
    for (let i = 0; i < hierarchiesList.length; i += 1) {
      const h = hierarchiesList[i];
      if (h.some((d) => Boolean(filters[d]))) {
        return i;
      }
    }
    return null;
  }, [filters, hierarchiesList]);

  const breadcrumb = useMemo((): DrillFilter[] => {
    // Retorna todos os filtros ativos (cross-group coexiste)
    return Object.values(filters) as DrillFilter[];
  }, [filters]);

  const drillLevel = breadcrumb.length;

  const apiFilters = useMemo((): Record<string, string> => {
    const next: Record<string, string> = {};
    (Object.values(filters) as DrillFilter[]).forEach((filter) => {
      // Multi-valores separados por pipe para o backend
      next[`filter_${filter.dimension}`] = filter.values.join("|");
    });
    return next;
  }, [filters]);

  const apiFiltersKey = useMemo(() => {
    // Ordena para manter assinatura estável e evitar "pisca-pisca" por re-fetch duplicado.
    const entries = Object.entries(apiFilters).sort(([a], [b]) =>
      a.localeCompare(b),
    );
    return JSON.stringify(entries);
  }, [apiFilters]);

  const selectRow = useCallback(
    (
      dimension: DrillDimension,
      value: string,
      label?: string,
      multiSelect?: boolean,
    ) => {
      // Localiza o grupo (hierarquia) ao qual a dimensão pertence
      const hierarchyInfo = (() => {
        for (let i = 0; i < hierarchiesList.length; i += 1) {
          const h = hierarchiesList[i];
          const idx = h.indexOf(dimension);
          if (idx >= 0) {
            return { groupIndex: i, hierarchy: h, dimensionIndex: idx };
          }
        }
        return null;
      })();

      setFilters((prev) => {
        const next: Record<string, DrillFilter> = { ...prev };

        // Se multiSelect (Ctrl+click), adiciona/remove valor no filtro existente
        if (multiSelect && prev[dimension]) {
          const existing = prev[dimension];
          const alreadySelected = existing.values.includes(value);

          if (alreadySelected) {
            // Remove o valor da seleção
            const newValues = existing.values.filter((v) => v !== value);
            if (newValues.length === 0) {
              // Se não sobrou nenhum valor, remove o filtro inteiro
              delete next[dimension];
            } else {
              next[dimension] = {
                dimension,
                value: newValues[newValues.length - 1],
                values: newValues,
                label: newValues.join(", "),
              };
            }
          } else {
            // Adiciona novo valor à seleção
            const newValues = [...existing.values, value];
            next[dimension] = {
              dimension,
              value,
              values: newValues,
              label: newValues.join(", "),
            };
          }
          return next;
        }

        // Seleção simples (sem Ctrl):
        // Toggle - se já está selecionado com exatamente esse valor, remove
        if (
          prev[dimension]?.values.length === 1 &&
          prev[dimension]?.values[0] === value
        ) {
          delete next[dimension];
          return next;
        }

        // Se pertence a uma hierarquia, limpa descendentes do MESMO grupo
        if (hierarchyInfo) {
          const { hierarchy: h, dimensionIndex } = hierarchyInfo;
          // Remove descendentes (níveis abaixo) no mesmo grupo
          for (const d of h.slice(dimensionIndex + 1)) {
            delete next[d];
          }
        }

        // Define o filtro atual (substitui qualquer valor anterior)
        next[dimension] = {
          dimension,
          value,
          values: [value],
          label: label || value,
        };

        return next;
      });
    },
    [hierarchiesList],
  );

  const clearFilter = useCallback(
    (dimension: DrillDimension) => {
      const hierarchyInfo = (() => {
        for (let i = 0; i < hierarchiesList.length; i += 1) {
          const h = hierarchiesList[i];
          const idx = h.indexOf(dimension);
          if (idx >= 0) {
            return { groupIndex: i, hierarchy: h, dimensionIndex: idx };
          }
        }
        return null;
      })();

      setFilters((prev) => {
        const next = { ...prev };
        delete next[dimension];

        // Se pertence a uma hierarquia, remove também os descendentes
        if (hierarchyInfo) {
          const { hierarchy: h, dimensionIndex } = hierarchyInfo;
          for (const d of h.slice(dimensionIndex + 1)) {
            delete next[d];
          }
        }

        return next;
      });
    },
    [hierarchiesList],
  );

  const clearFilterValue = useCallback(
    (dimension: DrillDimension, value: string) => {
      setFilters((prev) => {
        const existing = prev[dimension];
        if (!existing) return prev;

        const newValues = existing.values.filter((v) => v !== value);
        if (newValues.length === 0) {
          const next = { ...prev };
          delete next[dimension];
          return next;
        }

        return {
          ...prev,
          [dimension]: {
            ...existing,
            value: newValues[newValues.length - 1],
            values: newValues,
            label: newValues.join(", "),
          },
        };
      });
    },
    [],
  );

  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const navigateTo = useCallback(
    (level: number) => {
      if (level < 0) {
        clearAllFilters();
        return;
      }

      // Se não há hierarquia ativa, não há "drill-up" coerente
      if (activeHierarchyIndex === null) {
        clearAllFilters();
        return;
      }

      const h = hierarchiesList[activeHierarchyIndex];
      const keepDims = h.slice(0, level);
      setFilters((prev) => {
        const next: Record<string, DrillFilter> = {};
        for (const d of keepDims) {
          if (prev[d]) {
            next[d] = prev[d];
          }
        }
        return next;
      });
    },
    [activeHierarchyIndex, clearAllFilters, hierarchiesList],
  );

  const getApiFilters = useCallback(
    (): Record<string, string> => apiFilters,
    [apiFilters],
  );

  const isSelected = useCallback(
    (dimension: DrillDimension, value: string): boolean => {
      return filters[dimension]?.values.includes(value) ?? false;
    },
    [filters],
  );

  const value: DrilldownContextValue = {
    filters,
    drillLevel,
    breadcrumb,
    selectRow,
    clearFilter,
    clearFilterValue,
    clearAllFilters,
    navigateTo,
    apiFilters,
    apiFiltersKey,
    getApiFilters,
    isSelected,
  };

  return (
    <DrilldownContext.Provider value={value}>
      {children}
    </DrilldownContext.Provider>
  );
}

/**
 * Hook para acessar o contexto de drill-down.
 *
 * @example
 * ```tsx
 * const { selectRow, filters, breadcrumb } = useDrilldown();
 * ```
 */
export function useDrilldown(): DrilldownContextValue {
  const context = useContext(DrilldownContext);

  if (!context) {
    throw new Error("useDrilldown must be used within a DrilldownProvider");
  }

  return context;
}

/**
 * Hook opcional que não lança erro se usado fora do provider.
 * Útil para componentes que podem ou não estar em contexto de drill-down.
 */
export function useDrilldownOptional(): DrilldownContextValue | null {
  return useContext(DrilldownContext) ?? null;
}
